import { reduxBatch } from '@manaflair/redux-batch'
import PropTypes from 'prop-types'
import React, { memo, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Accordion,
  ButtonGroup,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
} from '../../../../../../../components'
import { IJOIST_INPUT_TYPE, MEASURE_UNITS } from '../../../../../../../utils/constants'
import { RenderAorB, RenderIf, isOneDropdownOption } from '../../../../../../../utils/fns'
import {
  CARRIED_MEMBER,
  CONFIGURATION,
  CONNECTION_TYPE,
  IJOIST_INPUT_OPTIONS,
  JOIST_MEMBER_TYPE,
  MEMBER_TYPE,
  TRUSS_TYPE_LABEL,
} from '../../../../../common/constants'
import {
  getDefaultOptValue,
  getDefaultPlyValue,
  getGAValue,
  parseFeetInchesSixteenths,
  parseFieldName,
  shouldDisplayNumberOfPlies
} from '../../../../../common/fns'
import { useCalcActions, useCommonOptions } from '../../../../../common/hooks'
import { truss as trussData } from '../../../../../common/json/carried-member.json'
import { memberIcons } from '../../../../../common/json/common.json'
import { helpText } from '../../../../../common/json/helpText.json'
import { getGirderState, getTrussState } from '../../../../../store/selectors'
import {
  jackActions,
  leftHipActions,
  rightHipActions,
  trussActions,
} from '../../../../../store/slices'
import DuplicateInputs from './DuplicateInputs'
import IJoistSelector from './IJoistSelector'
import MemberAngle from './MemberAngle'
import MemberLoads from './MemberLoads'

const Truss = (props) => {
  // const memberIDRef = useRef(null)
  // const depthRef = useRef(null)
  const dispatch = useDispatch()

  const { actions, selector, label, memberName, configuration, connectionType, country } = props
  const { isDuplicate, ...member } = useSelector(selector)

  const girder = useSelector(getGirderState)

  const { errors, validateField, setCalcValue, updateCalcValue } = useCalcActions(actions)
  const { materialOptions, widthOptions, pliesOptions, ...optionsData } = useCommonOptions(
    member,
    country,
    trussData,
  )

  const handleChangeType = (e) => {
    const defaultValue = { ...trussData.defaultValue[e.target.value] }

    if (label === CARRIED_MEMBER.TRUSS) {
      updateCalcValue(defaultValue)
    } else {
      reduxBatch([
        dispatch(jackActions.updateCalculation(defaultValue)),
        dispatch(leftHipActions.updateCalculation(defaultValue)),
        dispatch(rightHipActions.updateCalculation(defaultValue)),
      ])
    }
  }

  const handleChangeMaterial = (e) => {
    if (label === CARRIED_MEMBER.TRUSS) {
      updateCalcValue({ [e.target.name]: e.target.value })
    } else {
      const updatedValue = { [parseFieldName(e.target.name)]: e.target.value }
      reduxBatch([
        dispatch(jackActions.updateCalculation(updatedValue)),
        dispatch(leftHipActions.updateCalculation(updatedValue)),
        dispatch(rightHipActions.updateCalculation(updatedValue)),
      ])
    }
  }

  const handleChangeDepth = (e) => {
    const value = parseFeetInchesSixteenths(e.target.value)

    validateField(e)
    updateCalcValue({ depth: value })
  }

  const filterdMemberTypeOptions = useMemo(() => {
    if (label === CARRIED_MEMBER.TRUSS) return trussData.memberTypeData

    return trussData.memberTypeData.filter(
      (material) => material.value !== JOIST_MEMBER_TYPE.IJOIST,
    )
  }, [])

  const isControlledByJack = useMemo(
    () => configuration?.toLowerCase().includes('center') && label !== CARRIED_MEMBER.JACK,
    [configuration, label],
  )
  const isControlledByLeft = useMemo(
    () => configuration === CONFIGURATION.LEFT_RIGHT && label === CARRIED_MEMBER.RIGHT_HIP,
    [configuration, label],
  )

  const labelControlledAddon = useMemo(() => {
    if (isControlledByJack) {
      // if (label === 'rightHip') {
      //   return `(Controlled by the ${isDuplicate ? 'Left' : 'Jack'} inputs)`
      // }
      return '(Controlled by the Jack inputs)'
    } else if (isControlledByLeft) {
      return '(Controlled by the Left Hip inputs)'
    }

    return ''
  }, [isControlledByJack, isControlledByLeft])

  const isControlled = useMemo(() => {
    return isControlledByJack || isControlledByLeft
  }, [isControlledByJack, isControlledByLeft])

  const isOptionDisabled = useMemo(() => isDuplicate || isControlled, [isDuplicate, isControlled])

  useEffect(() => {
    const isWidthExists = widthOptions.some((item) => item.value === member.width)

    if (!isWidthExists && widthOptions.length > 0) {
      const value = getDefaultOptValue(widthOptions)
      dispatch(actions.modifyCalculation({ name: 'width', value }))
    }
  }, [member.material, widthOptions])

  useEffect(() => {
    if (member.type !== MEMBER_TYPE.TRUSS) {
      const isDepthExists = optionsData.depthOptions.some((item) => item.value === member.depth)

      if (!isDepthExists && optionsData.depthOptions.length > 0) {
        const value = getDefaultOptValue(optionsData.depthOptions)
        dispatch(actions.modifyCalculation({ name: 'depth', value }))
      }
    }
  }, [member.width, optionsData.depthOptions])

  useEffect(() => {
    const isPlyExists = pliesOptions.some((item) => item.value === member.ply)

    if (!isPlyExists) {
      dispatch(actions.modifyCalculation({ name: 'ply', value: getDefaultPlyValue(pliesOptions) }))
    }
  }, [member.depth, pliesOptions])

  useEffect(() => {
    const isThicknessExists = optionsData.thicknessOptions.some(
      (item) => item.value === member.thickness,
    )

    if (!isThicknessExists && optionsData.thicknessOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.thicknessOptions)
      dispatch(actions.modifyCalculation({ name: 'thickness', value }))
    }
  }, [member.material, optionsData.thicknessOptions])

  return (
    <Accordion icon={memberIcons[label]?.src} title={`${memberName} (Carried Member)`}>
      {member.type === JOIST_MEMBER_TYPE.IJOIST ? (
        <ButtonGroup
          className="row"
          colClassName="col-6"
          name="ijoistInputType"
          options={IJOIST_INPUT_OPTIONS}
          value={member.ijoistInputType}
          onChange={(name, value) => updateCalcValue({ [name]: value })}
          containerProps={{ 'data-testid': `${label}.ijoistInputType` }}
        />
      ) : null}

      <div className="grid grid__auto--250 grid--gap-1-2">
        {[CONFIGURATION.LEFT_RIGHT, CONFIGURATION.LEFT_CENTER_RIGHT].includes(configuration) &&
        typeof isDuplicate === 'boolean' ? (
          <div className="col-span-full">
            <DuplicateInputs isDuplicate={isDuplicate} onChange={updateCalcValue} />
          </div>
        ) : null}

        <FormGroup>
          <FormLabel htmlFor={`${label}.type`}>{`Member Type ${labelControlledAddon}`}</FormLabel>
          <Dropdown
            name={`${label}.type`}
            value={member.type}
            options={filterdMemberTypeOptions}
            onChange={handleChangeType}
            disabled={isOptionDisabled || girder.type !== 'solidSawn'}
            data-ga4={`${label}_memberType:${TRUSS_TYPE_LABEL[member.type]}`}
            data-testid={`${label}.type`}
          />
          {RenderIf(
            girder.type !== 'solidSawn',
            <span className="input-error is-visible mt-1">
              For non-Truss connections, use the Joist Connection Type
            </span>,
          )}
        </FormGroup>

        {RenderAorB(
          member.type === JOIST_MEMBER_TYPE.IJOIST &&
            member.ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER,
          <IJoistSelector
            label={label}
            material={member.material}
            manufacturer={member.manufacturer}
            series={member.series}
            model={member.model}
            width={member.width}
            onChange={updateCalcValue}
          />,
          <>
            <FormGroup>
              <FormLabel
                htmlFor={`${label}.material`}
              >{`Lumber Species ${labelControlledAddon}`}</FormLabel>
              <Dropdown
                name={`${label}.material`}
                options={materialOptions}
                value={member.material}
                onChange={handleChangeMaterial}
                disabled={isOptionDisabled || isOneDropdownOption(materialOptions)}
                data-ga4={`${label}_lumberSpecies:${getGAValue(materialOptions, member.material)}`}
                data-testid={`${label}.material`}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor={`${label}.width`}>
                {member.type === JOIST_MEMBER_TYPE.IJOIST ? 'Width' : 'Bottom Chord Width'}
              </FormLabel>
              <Dropdown
                name={`${label}.width`}
                options={widthOptions}
                value={member.width}
                onChange={setCalcValue}
                disabled={isDuplicate || isOneDropdownOption(widthOptions)}
                data-ga4={`${label}_width:${getGAValue(widthOptions, member.width)}`}
                data-testid={`${label}.width`}
              />
            </FormGroup>

            {member.type !== 'truss' ? (
              <FormGroup>
                <FormLabel htmlFor={`${label}.depth`}>Depth</FormLabel>
                <Dropdown
                  name={`${label}.depth`}
                  value={member.depth}
                  options={optionsData.depthOptions}
                  onChange={setCalcValue}
                  disabled={isDuplicate || isOneDropdownOption(optionsData.depthOptions)}
                  data-ga4={`${label}_depth:${getGAValue(optionsData.depthOptions, member.depth)}`}
                  data-testid={`${label}.depth`}
                />
              </FormGroup>
            ) : (
              <FormGroup>
                <FormLabel htmlFor={`${label}.depth`} popperText={helpText[country].totalHeight}>
                  Heel Height
                </FormLabel>
                <FormControl
                  name={`${label}.depth`}
                  value={member.depth}
                  onChange={handleChangeDepth}
                  addonAfter={MEASURE_UNITS.INCH}
                  error={errors[`${label}.depth`]}
                  disabled={isDuplicate}
                  data-ga4={`${label}_depth:${member.depth}`}
                  data-testid={`${label}.depth`}
                />
              </FormGroup>
            )}
          </>,
        )}

        {member.ijoistInputType === IJOIST_INPUT_TYPE.MANUALLY &&
        member.type === JOIST_MEMBER_TYPE.IJOIST ? (
          <FormGroup label="Flange Thickness">
            <FormLabel htmlFor={`${label}.thickness`}>Flange Thickness</FormLabel>
            <Dropdown
              name={`${label}.thickness`}
              value={member.thickness}
              options={optionsData.thicknessOptions}
              onChange={setCalcValue}
              disabled={isOneDropdownOption(optionsData.thicknessOptions)}
              data-ga4={`${label}_thickness:${getGAValue(
                optionsData.thicknessOptions,
                member.thickness,
              )}`}
              data-testid={`${label}.thickness`}
            />
          </FormGroup>
        ) : null}

        {shouldDisplayNumberOfPlies(member.type, pliesOptions) && (
          <FormGroup>
            <FormLabel htmlFor={`${label}.ply`}>Number of Plies</FormLabel>
            <Dropdown
              name={`${label}.ply`}
              options={pliesOptions}
              value={member.ply}
              onChange={setCalcValue}
              disabled={isDuplicate || isOneDropdownOption(pliesOptions)}
              data-ga4={`${label}_ply:${getGAValue(pliesOptions, member.ply)}`}
              data-testid={`${label}.ply`}
            />
          </FormGroup>
        )}

        <FormGroup>
          <FormLabel htmlFor={`${label}.memberID`}>Member ID</FormLabel>
          <FormControl
            name={`${label}.memberID`}
            value={member.memberID}
            onChange={(e) => {
              validateField(e)
              setCalcValue(e)
            }}
            error={errors[`${label}.memberID`]}
            data-ga4={`${label}_memberID:${member.memberID}`}
            data-testid={`${label}.memberID`}
          />
        </FormGroup>

        <MemberLoads
          label={label}
          country={country}
          load={member.load}
          uplift={member.uplift}
          onChange={setCalcValue}
          onValidateField={validateField}
          errors={errors}
          isDuplicate={isDuplicate}
        />

        {RenderIf(
          connectionType === CONNECTION_TYPE.MULTI_TRUSS,
          <MemberAngle
            label={label}
            slopeAngle={member.slopeAngle}
            skewAngle={member.skewAngle}
            isDuplicate={isDuplicate}
            onChange={updateCalcValue}
            onValidateField={validateField}
            errors={errors}
          />,
        )}
      </div>
    </Accordion>
  )
}

Truss.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any),
  selector: PropTypes.func,
  label: PropTypes.string.isRequired,
  memberName: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  configuration: PropTypes.string,
  connectionType: PropTypes.string,
}

Truss.defaultProps = {
  memberName: 'Truss',
  label: CARRIED_MEMBER.TRUSS,
  configuration: null,
  actions: trussActions,
  selector: getTrussState,
}

export default memo(Truss)
